import { date, type Dayjs } from 'configuration/dayjs'

/**
 * Date ranges defined as a count of whole weeks or whole calendar months.
 *
 * For presets read from a bucketed source, where naming a number of *days* delivers a varying
 * number of buckets depending on the weekday: 90 days covers 13 whole weeks most of the year and
 * 12 on the rest, so the window length oscillates. Naming the bucket count instead makes the
 * window deterministic and its trailing bucket always complete.
 */

/** Saturday of the week `day` falls in. `.day()` rather than `endOf('week')`, which is locale-aware. */
const weekSaturday = (day: Dayjs): Dayjs => day.startOf('day').day(6)

/**
 * The last `weeks` whole Sunday-Saturday weeks ending at or before `fromDate`.
 */
export function weekBucketRange(
    weeks: number,
    fromDate?: string | Dayjs
): [Dayjs, Dayjs] {
    const from = date(fromDate).startOf('day')
    const saturday = weekSaturday(from)
    // The Saturday runs forward past `from` unless `from` is already a Saturday.
    const end = saturday.isAfter(from) ? saturday.subtract(1, 'week') : saturday

    return [end.subtract(weeks * 7 - 1, 'days'), end]
}

/**
 * The last `months` whole calendar months ending at or before `fromDate`.
 *
 * Differs from `monthRange`, which always ends at the month *before* `fromDate`'s month and so
 * drops a complete month whenever the data boundary lands on a month end. Bucketed sources need
 * the last month that is actually complete, the same rule `weekBucketRange` applies to weeks.
 */
export function monthBucketRange(
    months: number,
    fromDate?: string | Dayjs
): [Dayjs, Dayjs] {
    const from = date(fromDate).startOf('day')
    const monthEnd = from.endOf('month').startOf('day')
    // endOf('month') runs forward past `from` unless it is already the last day of the month.
    const end = monthEnd.isAfter(from)
        ? from.startOf('month').subtract(1, 'day')
        : monthEnd

    return [end.startOf('month').subtract(months - 1, 'months'), end]
}
